import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCourseList } from '../../../hooks/useCourseList';
import { getNavigationItems } from './navigationItems';
import DesktopSidebar from './DesktopSidebar';
import DesktopHeader from './DesktopHeader';
import MobileHeader from './MobileHeader';
import MobileNav from './MobileNav';
import HowItWorksModal from './HowItWorksModal';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);

  const { user, logout, isInstructor } = useAuth();

  const location = useLocation();

  // Instructor accounts with student access can switch between views
  const canSwitchToStudentView = isInstructor && !!user?.has_student_access;

  // Determine which dashboard is currently being viewed — checked against
  // every student-only route, not just the dashboard itself, so a dual-role
  // account doesn't get bounced back to the instructor nav just by visiting
  // /courses.
  const studentOnlyPathPrefixes = [
    '/student-dashboard',
    '/courses',
    '/skills',
    '/badges',
    '/my-skill-videos',
  ];
  const viewingAsStudent =
    canSwitchToStudentView &&
    studentOnlyPathPrefixes.some((prefix) => location.pathname.startsWith(prefix));

  const displayAsInstructor = isInstructor && !viewingAsStudent;

  const navigationItems = getNavigationItems(displayAsInstructor);

  // Fetched once here (not lazily inside CourseSearch) so the desktop and
  // mobile header — both always mounted, just CSS-hidden by breakpoint —
  // share one course list instead of each fetching it independently.
  const { courses, loading, error, ensureLoaded } = useCourseList(displayAsInstructor, {
    eager: false,
  });
  const courseSearch = { courses, loading, error, ensureLoaded };

  const handleLogout = (): void => {
    logout();
  };

  return (
    <nav className="bg-[#F7F6F2]">
      {/* desktop layout*/}

      <div className="hidden md:block">
        <DesktopSidebar
          displayAsInstructor={displayAsInstructor}
          navigationItems={navigationItems}
          currentPath={location.pathname}
          onHelpClick={() => setShowHelpModal(true)}
          onLogout={handleLogout}
        />

        <DesktopHeader
          user={user}
          displayAsInstructor={displayAsInstructor}
          canSwitchToStudentView={canSwitchToStudentView}
          viewingAsStudent={viewingAsStudent}
          courseSearch={courseSearch}
          onHelpClick={() => setShowHelpModal(true)}
          onLogout={handleLogout}
        />
      </div>

      {/* mobile header: menu on the left, search, profile on the right */}

      <MobileHeader
        isNavOpen={isOpen}
        onToggleNav={() => setIsOpen(!isOpen)}
        onCloseNav={() => setIsOpen(false)}
        displayAsInstructor={displayAsInstructor}
        canSwitchToStudentView={canSwitchToStudentView}
        viewingAsStudent={viewingAsStudent}
        courseSearch={courseSearch}
        user={user}
        onHelpClick={() => setShowHelpModal(true)}
        onLogout={handleLogout}
      />

      {/* mobile navigation drawer */}

      {isOpen && (
        <MobileNav
          navigationItems={navigationItems}
          currentPath={location.pathname}
          onClose={() => setIsOpen(false)}
        />
      )}

      {/* how it works Modal*/}

      {showHelpModal && <HowItWorksModal onClose={() => setShowHelpModal(false)} />}
    </nav>
  );
};

export default Navigation;
